/**
 * TOSS Web Application — HintTooltip: объяснение по наведению, а не строкой под контролом
 *
 * Решение владельца 25.08.2026: постоянная серая фраза, объясняющая экран, ему мешает — «хрень»,
 * даже когда текст верный. Рабочая замена, которую он выбрал сам: иконка ⓘ рядом с контролом и
 * текст ПО НАВЕДЕНИЮ. Не раскрывающаяся по клику панель — он поправил и это.
 *
 * Кнопка, а не `<span title=…>`: системная подсказка появляется через секунду, не читается с
 * клавиатуры и не поддаётся палитре. Здесь же подсказка открывается и по фокусу, поэтому доступна
 * без мыши, и `aria-describedby` связывает её с контролом, который она объясняет.
 *
 * Полный текст объяснения остаётся в пустом состоянии экрана — там, где объяснение и есть
 * содержимое.
 *
 * 03.09.2026 компонент переехал из кабинета в общий кит под именем `HintTooltip`: тот же вопрос
 * встал в админке (прайс-лист), и второй такой же значок рядом означал бы две разные подсказки в
 * одном приложении. Тогда же появилась ширина `lg` — админская справка о том, как читается
 * матрица цен, в 224 px не укладывается.
 */

import { useCallback, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import { resolveHorizontalPosition } from './dropdownPosition';

export interface HintTooltipProps {
  /** Что рассказать. Одно-два предложения — это подсказка, а не справка. */
  text: ReactNode;
  /** Чем подсказка называется для скринридера: «почему это так» о чём именно. */
  label: string;
  /**
   * Насколько крупно.
   *
   *  - `inline` (по умолчанию) — 16-пиксельный глиф рядом со строкой текста: в форме сотрудника
   *    ⓘ стоит вплотную к названию переключателя и обязана быть меньше него.
   *  - `control` — ровно та же иконка и та же тач-цель, что у стрелки «назад» в шапке: глиф 24 px
   *    в области `--h-control`, цвет основного текста. Владелец 27.08.2026: «сделать иконки на
   *    одном расстоянии и размере, как иконка слева, которая назад». Выступ наружу (`-mr-2`)
   *    задаёт место применения — он нужен только в баре.
   */
  size?: 'inline' | 'control';
  /**
   * Насколько громко значок звучит в строке.
   *
   *  - `strong` — цвет основного текста; так ⓘ выглядит в баре кабинета, где он ровня стрелке
   *    «назад» и обязан читаться так же уверенно.
   *  - `quiet` — приглушённый, ярче только под курсором. Выбран владельцем 03.09.2026 для полосы
   *    прайс-листа: «зачем подсказка так сильно выделена визуально, она должна быть не такой
   *    заметной». Рядом с кнопкой «Фильтры» ⓘ — это сноска, а не действие.
   *
   * По умолчанию `inline` тихий, `control` громкий — ровно то, чем они были до появления пропа.
   */
  tone?: 'strong' | 'quiet';
  /**
   * Ширина окна подсказки: `sm` (224 px) — фраза-другая; `lg` (384 px) — справка в несколько
   * строк, как «что означают значки в матрице цен».
   */
  width?: 'sm' | 'lg';
  className?: string;
}

export function HintTooltip({
  text,
  label,
  size = 'inline',
  tone,
  width = 'sm',
  className = '',
}: HintTooltipProps): JSX.Element {
  const isControl = size === 'control';
  const isQuiet = (tone ?? (isControl ? 'strong' : 'quiet')) === 'quiet';
  const [isVisible, setVisible] = useState(false);
  /**
   * Сдвиг подсказки относительно иконки, в пикселях, и потолок ширины, когда места меньше неё.
   *
   * Центрирование по иконке (`left-1/2 -translate-x-1/2`) казалось нейтральным и было ошибкой: ⓘ
   * почти всегда стоит у ПРАВОГО края строки, и половина подсказки уезжала за экран, притаскивая
   * горизонтальную прокрутку (стенд, 27.08.2026). Сменивший его выбор стороны «по половине окна»
   * держался ровно до первой иконки в СЕРЕДИНЕ строки: в полосе прайс-листа ⓘ стоит третьим
   * элементом, `left-0` считался верным, и широкая подсказка на телефоне уходила на 144 px за
   * правый край (стенд, 03.09.2026).
   *
   * Теперь сторону и обрезку считает `resolveHorizontalPosition` — та же математика, что у
   * выпадающих слоёв, где оба края уже зажаты по краям окна. Ей нужна ИЗМЕРЕННАЯ ширина, поэтому
   * проход два: первый рендер невидим (`invisible`), в нём подсказку меряют, второй ставит её на
   * место.
   */
  const [placementStyle, setPlacementStyle] = useState<{ left: number; maxWidth?: number } | null>(
    null
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const id = useId();

  const show = useCallback((): void => {
    setPlacementStyle(null);
    setVisible(true);
  }, []);

  useLayoutEffect(() => {
    if (!isVisible || placementStyle != null) return;
    const trigger = buttonRef.current?.getBoundingClientRect();
    const width = tooltipRef.current?.offsetWidth ?? 0;
    if (!trigger || width <= 0) return;

    const position = resolveHorizontalPosition({
      // Сторона — это предпочтение, а не приговор: у края окна функция всё равно подвинет слой
      // внутрь. Иконка в правой половине окна тянет подсказку влево, в левой — вправо.
      placement: trigger.left > window.innerWidth / 2 ? 'bottom-end' : 'bottom-start',
      triggerLeft: trigger.left,
      triggerRight: trigger.right,
      dropdownWidth: width,
      viewportWidth: window.innerWidth,
    });

    setPlacementStyle({
      // Подсказка позиционируется абсолютно от иконки, а функция считает в координатах окна.
      left: (position.left ?? trigger.left) - trigger.left,
      ...(position.maxWidth != null ? { maxWidth: position.maxWidth } : {}),
    });
  }, [isVisible, placementStyle]);

  return (
    <span
      className={`relative inline-flex ${className}`}
      onMouseEnter={show}
      onMouseLeave={() => setVisible(false)}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-describedby={isVisible ? id : undefined}
        onFocus={show}
        onBlur={() => setVisible(false)}
        // На телефоне наведения нет — там подсказка открывается тапом и закрывается следующим.
        onClick={(event) => {
          event.preventDefault();
          if (isVisible) setVisible(false);
          else show();
        }}
        className={`inline-flex items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:text-[var(--color-primary-600)] ${
          isControl ? 'h-[var(--h-control)] w-[var(--h-control)] hover:bg-[var(--color-bg-secondary)]' : 'h-5 w-5'
        } ${
          isQuiet
            ? 'text-[var(--color-text-disabled)] hover:text-[var(--color-text-secondary)]'
            : 'text-[var(--color-text-primary)]'
        }`}
      >
        <Info
          className={isControl ? (isQuiet ? 'h-5 w-5' : 'h-6 w-6') : 'h-4 w-4'}
          aria-hidden="true"
        />
      </button>

      {isVisible && (
        <span
          ref={tooltipRef}
          id={id}
          role="tooltip"
          style={placementStyle ?? undefined}
          className={`
            absolute top-full z-50 mt-1 ${width === 'lg' ? 'w-96' : 'w-56'} max-w-[calc(100vw-1rem)]
            ${placementStyle == null ? 'invisible left-0' : ''}
            rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)]
            px-3 py-2 text-xs leading-snug text-[var(--color-text-secondary)]
            shadow-[var(--shadow-overlay)] pointer-events-none
          `}
        >
          {text}
        </span>
      )}
    </span>
  );
}

export default HintTooltip;
